/**
 * Phase 30 — Milestone TanStack Query hooks.
 *
 * Query key: ['milestones', projectId] — audience-agnostic per D-08 (the list
 * endpoint returns the same data for internal and external members; cache is
 * shared). The TRANSPORT, however, is audience-specific (external-board-
 * redirect-login debug session, 2026-07-02 — mirrors useBoard.ts's
 * BoardAudience branch): internal callers go through the internal
 * services/api/milestones.ts client.ts transport; external callers MUST use
 * the raw-fetch services/api/externalMilestones.ts transport so a 401 here
 * (expired session, revoked invite, …) never clears the internal auth token
 * or dispatches the global `auth:expired` navigation (Landmine #8 — see
 * client.ts:27-31 and externalBoard.ts's header comment). Passing the wrong
 * audience re-introduces that landmine — callers on the external surface
 * (KanbanSwimlane, ProjectListView's ExternalListSections,
 * TaskDetailSlideOver's MilestoneComboboxField) MUST pass 'external'.
 *
 * Cache invalidation rules (D-12):
 *   - create / update: invalidate ['milestones', projectId].
 *   - delete: invalidate ['milestones', projectId] AND both
 *     ['board', projectId, 'internal'] / ['board', projectId, 'external']
 *     (the FK ON DELETE SET NULL cleared milestoneId on tasks; the polled
 *     board needs to refetch to reflect the new null).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMilestones,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  type Milestone,
  type CreateMilestoneInput,
  type UpdateMilestoneInput,
} from '../services/api/milestones';
import { fetchMilestonesExternal } from '../services/api/externalMilestones';

export type MilestoneAudience = 'internal' | 'external';

export function useMilestones(projectId: string, audience: MilestoneAudience) {
  return useQuery<Milestone[]>({
    queryKey: ['milestones', projectId],
    queryFn:
      audience === 'internal'
        ? () => fetchMilestones(projectId)
        : () => fetchMilestonesExternal(projectId),
    enabled: Boolean(projectId),
    refetchOnWindowFocus: true,
  });
}

export function useCreateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMilestoneInput) => createMilestone(projectId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
    },
  });
}

export function useUpdateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateMilestoneInput }) =>
      updateMilestone(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
    },
  });
}

export function useDeleteMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteMilestone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
      // FK ON DELETE SET NULL cleared milestoneId on tasks — refetch board for
      // BOTH audiences so neither cache shows stale milestoneId references.
      queryClient.invalidateQueries({ queryKey: ['board', projectId, 'internal'] });
      queryClient.invalidateQueries({ queryKey: ['board', projectId, 'external'] });
    },
  });
}
